/* src/world/worldView.ts — the render loop and the world's moving parts.
 *
 * Owns exactly one requestAnimationFrame loop. Per frame it does four cheap things: advance
 * the camera rig, check whether the floating origin needs to move, ask the tile manager to
 * reconcile the visible set, and render. Everything expensive — decode, triangulation,
 * extrusion — already happened in a worker by the time anything here touches it.
 */

import { emit, on } from '../core/events.ts';
import { IS_DEV, SPAWN_LAT, SPAWN_LON } from '../core/env.ts';
import { stats } from '../core/stats.ts';
import {
  localToMerc,
  makeOrigin,
  maybeRebase,
  mercToLocal,
  toLocal,
  type Origin,
} from '../map/originRebase.ts';
import { tileBoundsLonLat, tileOriginMerc, tileSizeMerc, unproject } from '../map/tileMath.ts';
import { TilePipeline } from '../map/tilePipeline.ts';
import {
  Fog,
  Group,
  Mesh,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
  type PerspectiveCamera,
} from 'three';
import { CameraRig } from '../render/camera.ts';
import { createCartMarker } from '../render/cartMarker.ts';
import { createGlobeView } from '../render/globeView.ts';
import { createInteriorView } from '../render/interiorView.ts';
import { createParcelHighlight } from '../render/parcelHighlight.ts';
import { createMaterials } from '../render/materials.ts';
import { createPlaceLabels } from '../render/placeLabels.ts';
import { createScene } from '../render/scene.ts';
import { gameStore } from '../core/store.ts';
import { createAmbientLife } from './ambient.ts';
import { createContextLayer } from './contextLayer.ts';
import { TileManager, visibleTiles } from './tileManager.ts';

export interface WorldView {
  readonly canvas: HTMLCanvasElement;
  readonly rig: CameraRig;
  /** Set by the UI to receive per-frame tile counts. */
  onTileStatus: ((ready: number, total: number) => void) | null;
  start: () => void;
  /** Turn cart-placement picking on or off. While on, the orbit rig is detached. */
  setPlacementMode: (on: boolean) => void;
  /** Jump the floating origin to a new part of the country. Tiles stream in around it. */
  travelTo: (lon: number, lat: number) => void;
  /** Where the camera is looking right now, for persistence across reloads. */
  currentViewpoint: () => { lon: number; lat: number; distance: number };
  /**
   * A survey finished: refetch every resident tile the cell touches, with a fresh cache-bust
   * version so neither the browser nor the dev middleware serves the pre-survey emptiness.
   */
  invalidateRegion: (
    minLon: number, minLat: number, maxLon: number, maxLat: number, version: string,
  ) => void;
  dispose: () => void;
}

/** The camera distance above which the z13 skyline ring is streamed and shown. */
const FAR_RING_MIN_DISTANCE = 1100;

export function createWorldView(
  container: HTMLElement,
  startAt?: { readonly lon: number; readonly lat: number; readonly distance?: number },
): WorldView {
  const canvas = document.createElement('canvas');
  canvas.className = 'gf-canvas';
  container.appendChild(canvas);

  const { renderer, scene, camera, setSunTarget } = createScene(canvas);
  /*
   * Shadows re-render on OUR schedule, not three's — and the schedule is event-driven.
   * Every caster is static (buildings and the cart; cars and pedestrians receive but never
   * cast), so the map only goes stale when the texel-snapped frustum moves with the camera
   * (setSunTarget's return says so) or meshes enter/leave the scene (the managers' mutation
   * counters say so). The old policy — every second frame while streaming — re-rendered a
   * shadow map that LAGGED a moving camera by a frame, which made every shadow in view
   * judder at half the frame rate during exactly the WASD runs that stream tiles; and on
   * idle frames it paid for a full shadow pass nothing needed. Streaming into a stationary
   * view still throttles to every second frame (construction lag is invisible on a scene
   * visibly under construction), and a slow heartbeat catches the rare movers — cart
   * placement, the highlight — within a third of a second.
   */
  renderer.shadowMap.autoUpdate = false;
  let shadowParity = false;
  let shadowDirty = true;
  let lastMutations = -1;
  let frameCount = 0;
  const rig = new CameraRig(camera, canvas);
  if (startAt?.distance !== undefined) rig.distance = startAt.distance;
  rig.attach();

  // The right-click drag belongs to the camera; suppressing the native menu on the canvas
  // stops a pan from ending in a context menu. ui/dispatch.ts owns the menu everywhere else.
  canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
  });

  let origin: Origin = makeOrigin(startAt?.lon ?? SPAWN_LON, startAt?.lat ?? SPAWN_LAT);
  const materials = createMaterials();
  const pipeline = new TilePipeline();

  /*
   * Tile URL versioning, two axes. `TILE_SCHEMA` is baked into every URL and bumped whenever
   * the serving policy or translation changes — the edge cache keys on the full URL, so each
   * deploy naturally orphans the previous deployment's cached tiles instead of serving them
   * (headerless and policy-stale) for up to a day. `tileVersion` is per-session: bumped when
   * a survey lands, so tiles cached as empty before the crew reported back are refetched.
   */
  const TILE_SCHEMA = 'b2';
  let tileVersion = '';
  const tileUrl = (z: number, x: number, y: number): string => {
    // Same-origin, matching the original's observed path. Served by the Vite dev middleware
    // locally and by the Cloudflare Pages Function in production.
    const base = `/api/maptile/${String(z)}/${String(x)}/${String(y)}.mvt?s=${TILE_SCHEMA}`;
    return tileVersion === '' ? base : `${base}&v=${tileVersion}`;
  };

  // The street-level ring: full detail, exactly the streaming behaviour Phase 1 shipped.
  // It lives in its own group so it can stand down above ~9 km, where its content is
  // sub-pixel and the z13 ring already covers everything — that keeps the ceiling cheap.
  const nearRoot = new Group();
  nearRoot.name = 'nearRoot';
  scene.add(nearRoot);
  /*
   * The street ring's reach adapts to a triangle budget. Rural Australia holds the full
   * 3.4 km radius; a dense metro trims its outermost band — where a house is two pixels —
   * until the resident count sits inside the budget. On this hardware GPU buffers are
   * system RAM, so triangles ARE memory; this is the knob that keeps a Sydney from costing
   * three Perths. The scale moves a fraction of a percent per frame: no popping, just a
   * ring that breathes.
   */
  let nearReachScale = 1;
  const NEAR_TRI_BUDGET = 3_800_000;
  /*
   * The reach also TAPERS with altitude: from a 3 km-high camera, a house at the ring's
   * 3.4 km edge is a four-pixel speck the skyline ring already draws — but its dozen
   * micro-triangles still cost raster time, and at retina density the city overview is
   * raster-bound on exactly that. Full radius at street level; a tighter focus bubble as
   * the camera rises and the far ring carries more of the view. Named because the frame
   * loop reuses the same formula as the far-ring building cull's cap — if the two ever
   * disagreed, the cull would hide skyline the street ring is not actually drawing.
   */
  const nearMaxView = (cam: PerspectiveCamera): number => {
    const taper = Math.max(0.5, Math.min(1, 1 - (rig.distance - 1000) / 4000));
    return Math.min(3400 * nearReachScale * taper, Math.max(2000, cam.far * 0.6));
  };
  const tiles = new TileManager({
    scene: nearRoot, pipeline, materials, tileUrl,
    zoom: 15,
    maxView: nearMaxView,
    maxResident: 96,
  }, origin);

  /*
   * The skyline ring: z13 tiles carrying the arterial network and every building tall or big
   * enough to read from kilometres away (mvt_tile serves them from z12). It lives in its own
   * group so one flag hides it at street level and indoors. It sits 0.35 m below the street
   * ring: where the two transiently overlap while streaming, the full-detail geometry always
   * wins the depth test instead of z-fighting, and 35 cm is invisible at the distances the
   * ring is actually seen at.
   */
  const farRoot = new Group();
  farRoot.name = 'farRoot';
  farRoot.visible = false;
  scene.add(farRoot);
  // Same adaptive-budget treatment as the street ring: over a metro like Sydney even the
  // skyline diet reaches millions of triangles at full radius, and triangles are RAM here.
  let farReachScale = 1;
  const FAR_TRI_BUDGET = 1_800_000;
  const farTiles = new TileManager({
    scene: farRoot, pipeline, materials, tileUrl,
    zoom: 13,
    // With the continent basemap behind it, this ring has content *everywhere* — reach out
    // to ~64 km so the view from the ceiling is city and country, not ring-edge. The 0.45
    // factor tracks the fog band (5.2/12 of the far plane): tiles past it were fully
    // dissolved in haze yet still cost full raster time.
    maxView: (cam) => Math.min(64000 * farReachScale, Math.max(6000, cam.far * 0.45)),
    maxResident: 170,
    yOffset: -0.35,
    announce: false,
    // Nothing in the far ring is clicked; its JS-side geometry frees itself after upload.
    pickable: false,
    // Its depthless ground paints before the street ring's (see tileMesh FLAT_ORDER).
    orderBias: -100,
    // And its depth-tested solids draw AFTER the street ring's, so the duplicate skyline
    // underneath the near tiles is early-z rejected instead of shaded then painted over.
    solidOrder: 5,
    // The sun's 520 m shadow frustum never leaves the street ring: this ring neither
    // casts into the map (a per-update re-draw it was paying) nor samples it per pixel.
    shadows: false,
  }, origin);

  // Cars on the roads, pedestrians on the footpaths — pure ambience, driven per frame in both
  // the city view and (so the street outside the windows lives) the interior.
  const ambient = createAmbientLife(scene);

  /*
   * The whole country, always resident: Natural Earth coastline, urban patches, state borders
   * and major highways as one static mesh set below the tile rings — so the far view past the
   * surveyed cells shows Australia, not void. Town names float over it at altitude.
   */
  const context = createContextLayer(scene, materials, origin);
  const placeLabels = createPlaceLabels(scene);

  /*
   * The top of the zoom ladder. Hit the flat world's ceiling and keep going: a fade, then the
   * planet — borders, names, and a gold dot per surveyed cell. Diving back in fades to the
   * flat world wherever the globe was focused. The fade covers the swap in both directions.
   */
  const globe = createGlobeView(canvas);
  const fade = document.createElement('div');
  fade.className = 'gf-mode-fade';
  container.appendChild(fade);
  let transitioning = false;

  // Pre-bake the planet during a quiet moment after boot and upload it off-screen: the
  // profiler caught a one-frame stall on first globe entry (a 4096×2048 upload), and idle
  // time is where one-time costs belong.
  window.setTimeout(() => {
    globe.requestTexture((w, h) => context.bakeGlobeTexture(w, h), renderer);
  }, 7000);

  const enterGlobe = (): void => {
    if (transitioning) return;
    transitioning = true;
    fade.classList.add('is-on');
    window.setTimeout(() => {
      const vp = view.currentViewpoint();
      rig.detach();
      canvas.style.cursor = 'grab';
      highlight.set([], origin);
      placeLabels.hide();
      // The whole flat city is invisible from orbit; hold none of it in memory up here.
      tiles.clear();
      farTiles.clear();
      globe.requestTexture((w, h) => context.bakeGlobeTexture(w, h));
      globe.enter(vp.lon, vp.lat);
      mode = 'globe';
      emit('world:globe', { on: true });
      fade.classList.remove('is-on');
      transitioning = false;
    }, 190);
  };

  const exitGlobe = (lon: number, lat: number): void => {
    if (transitioning) return;
    transitioning = true;
    fade.classList.add('is-on');
    window.setTimeout(() => {
      globe.exit();
      mode = 'city';
      emit('world:globe', { on: false });
      canvas.style.cursor = '';
      rig.attach();
      // Land just under the ceiling: high enough to keep the overview, low enough that the
      // globe does not immediately re-trigger (hysteresis against the 14900 entry gate).
      rig.distance = 12500;
      rig.pitch = 1.0;
      view.travelTo(lon, lat);
      fade.classList.remove('is-on');
      transitioning = false;
    }, 190);
  };

  /*
   * The player's cart. Its geographic position comes from the game state, so the marker is
   * driven by the store rather than by anything in render/ — and it is re-placed from lon/lat
   * on every rebase, like the tiles, so it cannot drift away from the world under it.
   */
  const cartMarker = createCartMarker(scene);
  cartMarker.setVisible(false);
  let cartLonLat: readonly [number, number] | null = null;
  let cartId: string | null = null;

  // The gold outline under whichever building the inspector is open on. Driven by an event so
  // the render side never imports the UI, the same boundary the cart click uses in reverse.
  const highlight = createParcelHighlight(scene);
  const offHighlight = on('world:highlight', ({ ring }) => {
    highlight.set(ring, origin);
  });

  /*
   * The walkable interior. It renders into this same canvas but into its own scene and camera;
   * the frame loop below draws it *instead of* the city while a building is entered. The trigger
   * is a UI event, so the render side never imports the inspector that fires it — the same one-way
   * boundary the cart click and the highlight already use (PLAN.md Part 2 §1).
   */
  const interior = createInteriorView(canvas);
  let mode: 'city' | 'interior' | 'globe' = 'city';
  const offEnterInterior = on('world:enterinterior', (payload) => {
    // The orbit rig and the first-person controller compete for the same keys and pointer, so
    // only one may be attached. Hand control to the interior for the duration of the visit.
    rig.detach();
    canvas.style.cursor = '';
    // The gold selection outline would otherwise glow through the windows at your feet.
    highlight.set([], origin);
    interior.enter(payload);
    mode = 'interior';
  });
  const offExitInterior = on('interior:exit', () => {
    if (mode !== 'interior') return;
    interior.exit();
    mode = 'city';
    rig.attach();
  });

  // Surveyed-cell positions for the globe's gold dots; main.ts fetches them on globe entry.
  const offGlobeDots = on('globe:dots', ({ points }) => {
    globe.setSurveyDots(points);
  });

  const unsubscribeCart = gameStore.select(
    (s) => s?.cart ?? null,
    (cart) => {
      // An unplaced cart is still in the player's bag: it has no coordinates, so there is
      // nothing to draw. The placement flow is what puts it on the map.
      if (!cart || !cart.placed || cart.lon === null || cart.lat === null) {
        cartMarker.setVisible(false);
        cartLonLat = null;
        cartId = cart?.id ?? null;
        return;
      }
      cartId = cart.id;
      cartLonLat = [cart.lon, cart.lat];
      cartMarker.place(cart.lon, cart.lat, origin);
      cartMarker.setCuisine(cart.cuisine);
      cartMarker.setVisible(true);
    },
  );

  /*
   * Placement mode. While it is on, the pointer picks a spot on the ground plane instead of
   * orbiting: each move reports lon/lat to the UI, which asks the server whether a cart may go
   * there, and a click commits. The world only knows about geometry — it emits coordinates and
   * lets ui/ decide what they mean (PLAN.md Part 2 §1).
   */
  const raycaster = new Raycaster();
  const pointer = new Vector2();

  let placing = false;
  const groundPlane = new Plane(new Vector3(0, 1, 0), 0);
  const hit = new Vector3();

  /** Screen point → lon/lat on the ground, or null when the ray misses (looking at sky). */
  const pickGround = (clientX: number, clientY: number): readonly [number, number] | null => {
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    if (!raycaster.ray.intersectPlane(groundPlane, hit)) return null;
    const [mx, my] = localToMerc(hit.x, hit.z, origin);
    return unproject(mx, my);
  };

  const onPlaceMove = (e: MouseEvent): void => {
    if (!placing) return;
    const ll = pickGround(e.clientX, e.clientY);
    if (ll) emit('world:placehover', { lon: ll[0], lat: ll[1] });
  };

  const onPlaceClick = (e: MouseEvent): void => {
    if (!placing) return;
    e.stopPropagation();
    const ll = pickGround(e.clientX, e.clientY);
    if (ll) emit('world:placepick', { lon: ll[0], lat: ll[1] });
  };

  // Capture phase so a placement click never also reaches the cart-open handler below.
  canvas.addEventListener('mousemove', onPlaceMove);
  canvas.addEventListener('click', onPlaceClick, true);

  /*
   * Click-to-open. A raycast against the one marker is cheap enough to run per click and needs
   * no id-colour picking pass (PLAN.md Part 2 §4) — that only earns its cost once there are
   * many pickable objects, which is Phase 4. Hit-test the whole group so the pin counts too:
   * it is the part visible from a distance, so it is the part players aim at.
   */
  const onCanvasClick = (e: MouseEvent): void => {
    if (placing) return;
    // Inside a building, a click belongs to the interior (it locks the pointer to look around),
    // not to the city raycast underneath.
    if (mode === 'interior') return;
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);

    // The cart wins over the building it is standing next to: it is the smaller target and the
    // one the player is more likely to be aiming at.
    if (cartId !== null && raycaster.intersectObject(cartMarker.object, true).length > 0) {
      emit('world:cartclick', { cartId });
      return;
    }

    /*
     * Otherwise, which building? A raycast against the tile meshes gives a triangle, and the
     * triangle's first vertex carries the OSM way id of the footprint it was extruded from
     * (`geometry.userData.featureIds`, stashed in render/tileMesh.ts). So a click resolves to an
     * exact building rather than "whatever is nearest the point on the ground" — which matters
     * on a terraced street where footprints share a wall.
     *
     * A full-scene raycast is fine here because it happens on a click, not per frame, and Three
     * culls by bounding sphere before testing a single triangle. Only the two solid layers are
     * candidates: a click that lands on grass or a road is not a click on a building.
     */
    /*
     * Refresh the pick targets' world matrices before the ray. Tile meshes set
     * `matrixAutoUpdate = false`, so their `matrixWorld` is only recomputed during a render — and
     * a click that lands the same frame a tile streams in (or while the tab was backgrounded and
     * rendering was paused) would raycast that tile at a stale position and miss the building
     * that is plainly under the cursor. Raycaster never updates matrices itself, so we do, once,
     * on the handful of visible solid meshes. Cheap on a click; never on the frame path.
     */
    const targets = tiles.pickTargets();
    for (const t of targets) t.updateWorldMatrix(true, false);
    const pick = raycaster.intersectObjects(targets, false).find((i) => i.face != null);
    if (!pick?.face) return;

    const ids = pick.object instanceof Mesh
      ? (pick.object.geometry.userData['featureIds'] as Uint32Array | undefined)
      : undefined;
    const osmWayId = ids?.[pick.face.a] ?? null;

    const [mx, my] = localToMerc(pick.point.x, pick.point.z, origin);
    const [lon, lat] = unproject(mx, my);
    emit('world:buildingclick', { osmWayId: osmWayId === 0 ? null : osmWayId, lon, lat });
  };
  canvas.addEventListener('click', onCanvasClick);

  let running = false;
  let raf = 0;
  let announcedReady = false;
  // Camera-target velocity tracking for the tile look-ahead.
  let lastTargetX = 0;
  let lastTargetZ = 0;
  let velX = 0;
  let velZ = 0;
  // Backpressure on geometry uploads: collapses when frames run long, recovers when smooth.
  let uploadScale = 1;
  // Consecutive-ish >50 ms frames. Only a SUSTAINED run flips uploads into bulk mode —
  // a single transition spike must not (a big upload lengthens the frame, which would
  // grant a bigger budget, which lengthens the next: a feedback loop the probe caught
  // turning one long frame into a 500 ms one).
  let slowFrames = 0;

  /*
   * Which z14 survey cells can the camera see? Emitted for main.ts (which owns the network
   * side of coverage), throttled to every ~3 s and only when the answer changes. Nearest
   * cells first, so the queue drains outward from where the player is looking.
   */
  let lastCellsKey = '';
  // Wall-clock, not frames: under a throttled or struggling tab a frame countdown stretches
  // from seconds into minutes, and the survey watcher just… never engages.
  let nextCellsAt = 0;
  const emitViewCells = (camLon: number, camLat: number): void => {
    const raw = visibleTiles(camera, origin, 14, Math.min(2600, camera.far * 0.6), 24);
    const half = tileSizeMerc(14) / 2;
    const sorted = raw
      .map((c) => {
        const [mx, my] = tileOriginMerc(c.z, c.x, c.y);
        const [lx, lz] = mercToLocal(mx + half, my - half, origin);
        return { c, d: Math.hypot(lx - rig.target.x, lz - rig.target.z) };
      })
      .sort((a, b) => a.d - b.d)
      /*
       * Only cells where the street ring has actually shown a building. Surveys exist to make
       * buildings buyable; queueing empty desert wastes the crew's strictly-serial time and
       * puts a "surveying…" promise on screen that nothing will ever redeem — the exact
       * "it says surveying and nothing appears" complaint.
       */
      .filter(({ c }) => {
        const b = tileBoundsLonLat(c.z, c.x, c.y);
        return tiles.hasBuildingsWithin(b.minLon, b.minLat, b.maxLon, b.maxLat);
      })
      .slice(0, 12);
    const key = sorted.map((s) => `${String(s.c.x)}/${String(s.c.y)}`).join('|');
    if (key === lastCellsKey) return;
    lastCellsKey = key;
    emit('world:viewcells', {
      lon: camLon,
      lat: camLat,
      cells: sorted.map((s) => [s.c.x, s.c.y] as const),
    });
  };

  const view: WorldView = {
    canvas,
    rig,
    onTileStatus: null,

    start(): void {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    },

    travelTo(lon: number, lat: number): void {
      // Travelling while inside a building would leave the interior pointed at a city that is
      // no longer under it; the travel UI is hidden indoors, this is the belt to that brace.
      if (mode === 'interior') return;
      origin = makeOrigin(lon, lat);
      tiles.setOrigin(origin);
      farTiles.setOrigin(origin);
      context.setOrigin(origin);
      placeLabels.hide();
      rig.target.set(0, 0, 0);
      // Arrive with an overview: high enough to watch the area stream (and survey) in.
      rig.distance = Math.max(rig.distance, 2200);
      if (cartLonLat) cartMarker.place(cartLonLat[0], cartLonLat[1], origin);
      highlight.rebuild(origin);
      lastCellsKey = '';
      nextCellsAt = 0;
      emit('world:traveled', { lon, lat });
    },

    invalidateRegion(minLon, minLat, maxLon, maxLat, version): void {
      tileVersion = version;
      tiles.invalidateRegion(minLon, minLat, maxLon, maxLat);
      farTiles.invalidateRegion(minLon, minLat, maxLon, maxLat);
    },

    currentViewpoint(): { lon: number; lat: number; distance: number } {
      const [mx, my] = localToMerc(rig.target.x, rig.target.z, origin);
      const [lon, lat] = unproject(mx, my);
      return { lon, lat, distance: rig.distance };
    },

    setPlacementMode(on: boolean): void {
      placing = on;
      // The orbit rig would otherwise fight the placement pointer for the same drags.
      if (on) rig.detach();
      else rig.attach();
      canvas.style.cursor = on ? 'crosshair' : '';

      /*
       * Drop to street altitude when placement starts. At the default 900 m the camera covers
       * several blocks per screen inch, so a click lands nowhere near the 25 m snap radius and
       * the player is told "no footpath here" over and over while doing nothing wrong. Coming
       * down to ~150 m makes individual pavements pickable, which is the scale the decision is
       * actually made at.
       */
      if (on) {
        rig.distance = 150;
        rig.pitch = 0.95;
      }
    },

    dispose(): void {
      running = false;
      cancelAnimationFrame(raf);
      rig.detach();
      canvas.removeEventListener('click', onCanvasClick);
      canvas.removeEventListener('mousemove', onPlaceMove);
      canvas.removeEventListener('click', onPlaceClick, true);
      unsubscribeCart();
      offHighlight();
      offEnterInterior();
      offExitInterior();
      offGlobeDots();
      scene.remove(nearRoot);
      cartMarker.dispose();
      highlight.dispose();
      interior.dispose();
      globe.dispose();
      ambient.dispose();
      placeLabels.dispose();
      context.dispose();
      tiles.dispose();
      farTiles.dispose();
      scene.remove(farRoot);
      pipeline.dispose();
      materials.dispose();
      renderer.dispose();
      fade.remove();
      canvas.remove();
    },
  };

  // Deterministic camera control for CI and Playwright. Harmless in production: it only
  // moves a camera, and every value it can set is already reachable with a mouse.
  window.__gfCamera = (pose) => {
    if (pose.x !== undefined) rig.target.x = pose.x;
    if (pose.z !== undefined) rig.target.z = pose.z;
    if (pose.distance !== undefined) rig.distance = pose.distance;
    if (pose.pitch !== undefined) rig.pitch = pose.pitch;
    if (pose.yaw !== undefined) rig.yaw = pose.yaw;
  };
  window.__gfTiles = () => ({
    resident: tiles.residentCount,
    ready: tiles.readyCount,
    triangles: tiles.triangles,
    farResident: farTiles.residentCount,
    farReady: farTiles.readyCount,
    nearQueued: tiles.queuedCount,
    nearLoading: tiles.loadingCount,
    nearPending: tiles.pendingCommits,
    farQueued: farTiles.queuedCount,
    farLoading: farTiles.loadingCount,
    farPending: farTiles.pendingCommits,
  });
  window.__gfCameraState = () => ({
    x: rig.target.x,
    z: rig.target.z,
    yaw: rig.yaw,
    pitch: rig.pitch,
    distance: rig.distance,
  });
  window.__gfTravel = (lon: number, lat: number) => {
    view.travelTo(lon, lat);
  };
  window.__gfOrigin = () => ({ lon: origin.lon, lat: origin.lat });
  window.__gfMode = () => mode;
  window.__gfGlobe = () => (mode === 'globe' ? globe.focus : null);
  window.__gfMem = () => {
    // Chrome-only heap gauge; zero elsewhere. The renderer counts are exact everywhere.
    const heap =
      (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory
        ?.usedJSHeapSize ?? 0;
    return {
      heapMB: Math.round(heap / 1048576),
      geometries: renderer.info.memory.geometries,
      textures: renderer.info.memory.textures,
    };
  };

  // Dev-only scene handle. Invaluable for "which layer is that on screen?" questions, which
  // are otherwise answered by guessing. Not exposed in production builds.
  if (IS_DEV) {
    window.__gfScene = scene;
  }

  function frame(now: number): void {
    if (!running) return;
    raf = requestAnimationFrame(frame);

    const dt = stats.beginFrame(now);

    // The planet owns the frame while it is up; the flat world resumes untouched on dive.
    if (mode === 'globe') {
      // Bury the cleared city a few tiles per frame — doing all ~270 disposals in the
      // entry frame was a 200 ms hitch that outlived the fade meant to hide it.
      tiles.reap(24);
      farTiles.reap(24);
      globe.update(Math.min(dt, 0.1));
      const dive = globe.consumeDive();
      if (dive) exitGlobe(dive.lon, dive.lat);
      globe.render(renderer);
      stats.setRenderInfo(renderer.info);
      stats.endFrame();
      return;
    }

    /*
     * While a building is entered, the interior owns the frame — but the city does not vanish:
     * it is rendered first, from the player's exact pose inside the building, with that one
     * building hidden by feature id so its own exterior does not wrap the camera. The interior
     * scene (transparent background, real holes in the walls) then draws over it with a fresh
     * depth buffer, and the street outside shows through every window and the open door.
     * Streaming and rebase stay paused; the tiles resume untouched on exit.
     */
    if (mode === 'interior') {
      // The skyline ring is for the aerial view; through a window it is overdraw.
      farRoot.visible = false;
      interior.update(Math.min(dt, 0.1));
      const walked = interior.current;
      if (walked) {
        const [bx, bz] = toLocal(walked.lon, walked.lat, origin);
        const pose = interior.pose();
        // The street outside the windows keeps moving while the player is indoors.
        ambient.update(Math.min(dt, 0.1), bx, bz, tiles.ambientSources());
        renderer.shadowMap.needsUpdate = true;
        camera.rotation.order = 'YXZ';
        camera.position.set(bx + pose.x, pose.y, bz + pose.z);
        camera.rotation.set(pose.pitch, pose.yaw, 0);
        camera.near = 0.1;
        camera.far = 6000;
        camera.updateProjectionMatrix();
        setSunTarget(bx, bz);
        materials.setHiddenFeature(walked.osmWayId);
        renderer.autoClear = false;
        renderer.clear(true, true, false);
        renderer.render(scene, camera);
        materials.setHiddenFeature(null);
        renderer.clearDepth();
        interior.render(renderer);
        renderer.autoClear = true;
      } else {
        interior.render(renderer);
      }
      stats.setRenderInfo(renderer.info);
      stats.endFrame();
      return;
    }

    // Clamp dt: after a background tab or a long stall, an unclamped delta teleports the
    // camera across the city in one frame.
    rig.update(Math.min(dt, 0.1));

    /*
     * Fog follows the zoom. At street level this is exactly the fixed 1800–5200 band the city
     * shipped with; pulled up to the 15 km ceiling the band stretches to ~34 km so the whole
     * metro is in view before the horizon dissolves. Linear in rig distance, so there is
     * never a pop — and always inside the far plane (12 × distance).
     */
    if (scene.fog instanceof Fog) {
      scene.fog.near = Math.max(1800, rig.distance * 1.7);
      scene.fog.far = Math.min(90000, Math.max(5200, rig.distance * 5.2));
    }

    // The ceiling of the flat world is the floor of the globe.
    if (rig.distance >= 14900) enterGlobe();

    // Rebase before streaming, so the tile manager works against the origin this frame will
    // actually render with.
    const [camMercX, camMercY] = localToMerc(rig.target.x, rig.target.z, origin);
    const [camLon, camLat] = unproject(camMercX, camMercY);
    const next = maybeRebase(camLon, camLat, origin);
    if (next) {
      origin = next;
      tiles.setOrigin(origin);
      farTiles.setOrigin(origin);
      context.setOrigin(origin);
      // Re-derive the rig's target in the new frame rather than assuming it lands on zero:
      // the camera may be mid-gesture and its target is not exactly the rebase point.
      const [tx, tz] = toLocal(camLon, camLat, origin);
      rig.target.x = tx;
      rig.target.z = tz;
      // The cart is positioned in the old frame; re-derive it from lon/lat in the new one.
      if (cartLonLat) cartMarker.place(cartLonLat[0], cartLonLat[1], origin);
      // The highlight outline is stored in lon/lat too, so it survives the rebase the same way.
      highlight.rebuild(origin);
    }

    // Keep the sun's small shadow frustum centred on the look-at point, or shadows would
    // end at a hard line a few blocks from the target. True = the snapped frustum moved,
    // so the shadow map must re-render this frame to stay glued to the buildings.
    const sunMoved = setSunTarget(rig.target.x, rig.target.z);

    // The street ring stands down above ~9 km — its buildings are sub-pixel there and the
    // z13 ring (with the basemap behind it) is already carrying the whole view. Higher
    // still, its resident tiles are dropped outright: a dense CBD held invisible is
    // hundreds of megabytes, and the warm HTTP cache refills it in seconds on descent.
    const nearOn = rig.distance < 9000;
    nearRoot.visible = nearOn;
    /*
     * Look-ahead: smoothed camera-target velocity, so the tile bbox leans toward where the
     * player is going and the streets ahead of a WASD run are streaming before they appear.
     */
    const dtSafe = Math.max(1 / 240, Math.min(dt, 0.1));
    velX += (((rig.target.x - lastTargetX) / dtSafe) - velX) * 0.2;
    velZ += (((rig.target.z - lastTargetZ) / dtSafe) - velZ) * 0.2;
    lastTargetX = rig.target.x;
    lastTargetZ = rig.target.z;
    const biasScale = Math.min(1, 1800 / Math.max(1, Math.hypot(velX, velZ) * 1.6));
    const biasX = velX * 1.6 * biasScale;
    const biasZ = velZ * 1.6 * biasScale;

    /*
     * One upload budget for the whole frame, SHARED by both rings — each used to spend its
     * own 6 MB, and the frames where both stream (every zoom-out) paid double. Sized by
     * wall-clock so a 120 Hz display uploads the same bytes per second as a 60 Hz one
     * rather than double; and scaled down while recent frames ran long — at retina density
     * the base render leaves less headroom, and jank is exactly uploads spilling past it.
     * The street ring drains it first: it is the ground under the camera.
     */
    if (dt > 0.021) uploadScale = Math.max(0.2, uploadScale * 0.6);
    else if (dt < 0.0175) uploadScale = Math.min(1, uploadScale * 1.05);
    slowFrames = dt > 0.05 ? Math.min(60, slowFrames + 1) : Math.max(0, slowFrames - 2);
    const commitBudget = {
      /*
       * Two regimes. Frames near the vsync budget get the paced, backpressured allowance —
       * that is what keeps streaming invisible on a healthy machine. A SUSTAINED run of
       * >50 ms frames means the renderer itself is slow (software GL in CI, a
       * battery-throttled laptop, a background tab): pacing cannot make those smooth, it
       * can only starve content — the skyline e2e caught CI filling a city at 1/5 the old
       * rate. There, upload in proportion to the frame actually being paid for. Sustained
       * only: granting the bulk budget to a lone transition spike feeds the spike.
       */
      bytes: slowFrames > 15
        ? 360_000_000 * Math.min(dt, 0.25) * 0.5
        : 360_000_000 * Math.max(1 / 240, Math.min(dt, 1 / 60)) * uploadScale,
    };

    if (nearOn) {
      const nearTris = tiles.triangles;
      if (nearTris > NEAR_TRI_BUDGET) nearReachScale = Math.max(0.55, nearReachScale * 0.99);
      else if (nearTris < NEAR_TRI_BUDGET * 0.75) {
        nearReachScale = Math.min(1, nearReachScale * 1.004);
      }
      tiles.update(camera, biasX, biasZ, commitBudget);
    } else {
      // Dormant: keep resident tiles warm for the descent, but drain anything mid-pipeline
      // — an un-update()d ring never commits, so parked arrivals would pin memory forever.
      tiles.discardPending();
      if (rig.distance > 12000 && tiles.residentCount > 0) tiles.clear();
    }
    // The skyline ring only exists once the camera is high enough to be looking at distance.
    const farOn = rig.distance > FAR_RING_MIN_DISTANCE;
    farRoot.visible = farOn;
    if (farOn) {
      const farTris = farTiles.triangles;
      if (farTris > FAR_TRI_BUDGET) farReachScale = Math.max(0.4, farReachScale * 0.99);
      else if (farTris < FAR_TRI_BUDGET * 0.75) {
        farReachScale = Math.min(1, farReachScale * 1.004);
      }
      farTiles.update(camera, 0, 0, commitBudget);
      /*
       * Hide the far ring's building chunks wherever the street ring is already drawing
       * that ground in full detail — rasterising the duplicate skyline under a dense CBD
       * was the last ~5 ms between the mid-altitude overview and 60 fps at retina density.
       * Chunk-by-chunk against committed street tiles (tileManager.coversPoint), so a tile
       * streaming somewhere in view unhides only the chunks over itself, not the city.
       */
      farTiles.cullBuildingsCovered(
        nearOn ? (x, z) => tiles.coversPoint(x, z) : () => false,
      );
    } else {
      farTiles.discardPending();
    }
    // Bury cleared tiles a few per frame here too — the globe loop reaps its own, but a
    // graveyard filled right before a mode switch must not wait for the next globe visit.
    tiles.reap(8);
    farTiles.reap(8);

    // Coverage check every ~3 s: which survey cells is the player looking at? Only while the
    // street ring is live — its tiles are the evidence a cell has buildings worth surveying,
    // and from high altitude a "random place" is usually open country that needs none.
    if (now >= nextCellsAt && nearOn) {
      nextCellsAt = now + 3000;
      emitViewCells(camLon, camLat);
    }

    // Town names appear once the camera is high enough that towns are smudges.
    placeLabels.update(context.places, origin, rig.target.x, rig.target.z, rig.distance);

    ambient.update(Math.min(dt, 0.1), rig.target.x, rig.target.z, tiles.ambientSources());
    /*
     * The event-driven shadow schedule (see the createScene block up top for the policy).
     * Streamed-in geometry re-renders the map at half rate only at street level, where a
     * building materialising without its shadow would be conspicuous; from altitude — where
     * a whole city fill is in view and each shadow is a pixel — new meshes ride the slow
     * heartbeat and one flush when the fill settles. That difference is several ms of GPU
     * per fill frame at retina density.
     */
    frameCount += 1;
    shadowParity = !shadowParity;
    const mutations = tiles.mutations + farTiles.mutations;
    shadowDirty = shadowDirty || mutations !== lastMutations;
    lastMutations = mutations;
    const streamingNow = tiles.pendingCommits > 0 || farTiles.pendingCommits > 0;
    if (
      sunMoved ||
      (shadowDirty && shadowParity && rig.distance < 1200) ||
      (shadowDirty && !streamingNow) ||
      frameCount % 20 === 0
    ) {
      renderer.shadowMap.needsUpdate = true;
      shadowDirty = false;
    }
    renderer.render(scene, camera);
    stats.setRenderInfo(renderer.info);
    stats.endFrame();

    const ready = tiles.readyCount;
    view.onTileStatus?.(ready, tiles.residentCount);

    // The honest moment to call the world interactive is the first frame with real geometry
    // on screen — not the moment the canvas exists.
    if (!announcedReady && ready > 0) {
      announcedReady = true;
      emit('boot:progress', { phase: 'tiles', ratio: 1 });
      emit('boot:ready');
      document.documentElement.dataset['gfWorld'] = 'ready';
    }
  }

  return view;
}
